<template>
  <slide-out
    dock="right"
    :size="sideOutCurrentSize"
    ref="contractorSlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching Contractor ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold pa-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update: '{{ selected.number }}'
      </h3>
      <h3 v-else class="font-weight-bold pa-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Add Contractor
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div>
      <div class="text-center d-flex flex-column" v-if="isFetching">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading contractor data...</p>
      </div>
      <v-form v-show="!isFetching" ref="editContractorForm" class="pa-4" v-model="valid">
        <v-text-field
          dense
          outlined
          v-model="selected.number"
          label="Vendor/Contractor Number"
          placeholder="Vendor/Contractor Number"></v-text-field>
        <v-text-field
          dense
          outlined
          v-model="selected.companyName"
          :rules="[formRules.required]"
          label="Company Name *"
          placeholder="Company Name"></v-text-field>
        <v-text-field
          dense
          outlined
          v-model="selected.address.line1"
          label="Address"
          placeholder="Address"></v-text-field>
        <v-select
          :items="countries"
          dense
          outlined
          attach
          v-model="selected.address.country"
          label="Country"
          placeholder="Country">
        </v-select>
        <v-text-field
          dense
          outlined
          v-model="selected.phoneNumber"
          label="Telephone"
          placeholder="Telephone"></v-text-field>
        <v-text-field
          dense
          outlined
          v-model="selected.email"
          :rules="[formRules.validEmail, formRules.required]"
          label="Email *"
          placeholder="Email"></v-text-field>
        <v-select
          :items="paymentTerms"
          dense
          outlined
          attach
          v-model="selected.paymentTermText"
          label="Payment Terms"
          placeholder="Payment Terms">
        </v-select>
        <v-select
          :items="currencies"
          dense
          outlined
          attach
          v-model="selected.currency"
          :rules="[formRules.required]"
          label="Currency *"
          placeholder="Currency">
        </v-select>
        <v-textarea
          dense
          outlined
          v-model="selected.services"
          label="Contractual Services"
          placeholder="Description of services provided by the contractor.">
        </v-textarea>
        <v-select
          :items="rateList"
          dense
          outlined
          attach
          v-model="selected.performance"
          label="Contractor Performance Rating"
          placeholder="Contractor Performance Rating ">
        </v-select>
        <v-textarea
          dense
          outlined
          v-model="selected.obligations"
          label="Contractual Obligations"
          placeholder="Specify any contractual obligations or deliverables.">
        </v-textarea>
      </v-form>
    </div>

    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || slideouts.isLoading || !slideouts.hasChanges"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';

  import { $handleError, $isEqual, $formatDate } from '@Utils';

  export default {
    name: 'edit-contractor',
    data() {
      return {
        valid: false,
        formRules,
        isFetching: false,
        selected: {
          address: {
            city: '',
            country: '',
            line1: '',
            line2: '',
            postalCode: '',
            state: '',
            subdivision: '',
            lotNumber: '',
          },
        },
        countries: ['USA', 'Germany', 'UK', 'Japan'],
        paymentTerms: ['Net 30', 'Net 60', 'Net 90'],
        currencies: ['USD', 'EUR', 'GBP', 'JPY'],
        rateList: ['Excellent', 'Good', 'Fair', 'Poor'],
        modal: false,
        selectedCemented: {},
        slideouts: {
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
    },
    methods: {
      OnResize() {
        let contractorSlideout = this.$refs.contractorSlideout;
        this.sideOutCurrentSize = contractorSlideout.resizeValue;
      },
      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        this.slideouts.isLoading = true;
        let toSend = structuredClone(this.selected);
        this.$store
          .dispatch('CONTRACTOR/UPDATE', toSend)
          .then(() => {
            this.closeUpdateSlideout();
          })
          .catch((err) => {
            $handleError(err);
          })
          .finally(() => {
            this.slideouts.isLoading = false;
          });
      },
      closeUpdateSlideout() {
        this.selected = {
          address: {
            city: '',
            country: '',
            line1: '',
            line2: '',
            postalCode: '',
            state: '',
            subdivision: '',
            lotNumber: '',
          },
        };
        this.isFetching = false;
        this.cementContractor();
        this.slideouts.active = false;
        this.valid = true;
        this.$refs.editContractorForm.resetValidation();
      },
      open(id) {
        if (id != null) {
          this.contractorId = id;
          this.fetchContractor();
        }
        this.slideouts.active = true;
      },
      $formatDate,
      fetchContractor() {
        this.isFetching = true;
        this.$store
          .dispatch('CONTRACTOR/GET_BY_ID', this.contractorId)
          .then((resp) => {
            this.isFetching = false;
            this.slideouts.isLoading = false;
            const data = structuredClone(resp.data);
            data.licenseExpiryDate = $formatDate(data.licenseExpiryDate, 'YYYY-MM-DD');
            this.selected = data;
            if (!this.selected.address) {
              this.selected.address = {
                city: '',
                country: '',
                line1: '',
                line2: '',
                postalCode: '',
                state: '',
                subdivision: '',
                lotNumber: '',
              };
            }
            this.cementContractor();
          })
          .catch((err) => {
            this.isFetching = false;
            this.closeUpdateSlideout();
            $handleError(err);
          });
      },
      cementContractor() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.slideouts.active) return;
        this.slideouts.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
    },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
  };
</script>
